/**
 * Azioni "Ricontatta" per una chat WhatsApp: Lead/Cliente collegato, utente, avvisi.
 * La lettura periodica dei promemoria la fa useRecontactLoader (montato nella lista chat).
 */
import { useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '@/lib/auth';
import { useChatRecontactStore } from '@/lib/stores/chatRecontactStore';
import { useWhatsAppAgentStore } from '@/lib/stores/whatsappAgentStore';
import { formatRecontactDate } from '@/lib/services/whatsapp/recontactRules';

const RILETTURA_MS = 5 * 60 * 1000;

/** Legge i promemoria dell'organizzazione all'avvio, ogni 5 minuti e al ritorno sulla scheda. */
export function useRecontactLoader(organizationId: string | null | undefined) {
  const load = useChatRecontactStore((s) => s.load);
  useEffect(() => {
    if (!organizationId) return;
    void load(organizationId);
    const timer = setInterval(() => { if (!document.hidden) void load(organizationId); }, RILETTURA_MS);
    const onVisible = () => { if (!document.hidden) void load(organizationId); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [organizationId, load]);
}

export function useChatRecontact(chatKey: string, organizationId: string | null | undefined) {
  const { user } = useAuth();
  const reminder = useChatRecontactStore((s) => s.reminders.get(chatKey) ?? null);
  const metaAvailable = useChatRecontactStore((s) => s.metaAvailable);
  const scheduleStore = useChatRecontactStore((s) => s.schedule);
  const completeStore = useChatRecontactStore((s) => s.complete);
  const clientId = useWhatsAppAgentStore((s) => s.assignments.find((a) => a.chat_jid === chatKey)?.client?.id ?? null);

  const linked = !!clientId || !!reminder?.taskId;
  /** Senza Lead/Cliente il promemoria va sulla chat: serve la migration delle colonne recontact_*. */
  const canSave = !!organizationId && !!user?.id && (linked || metaAvailable || (!!reminder && !reminder.taskId));

  const schedule = useCallback(async (chatName: string, dueAt: Date, note: string | null) => {
    if (!organizationId || !user?.id) return false;
    const ok = await scheduleStore({ chatKey, chatName, dueAt, note, clientId, userId: user.id, organizationId });
    if (!ok) toast.error('Non sono riuscito a salvare il promemoria. Riprova tra poco.');
    else toast.success(`Promemoria impostato: ricontattare il ${formatRecontactDate(dueAt.toISOString())}${linked ? ' (Task commerciale creato)' : ''}`);
    return ok;
  }, [organizationId, user?.id, scheduleStore, chatKey, clientId, linked]);

  const complete = useCallback(async () => {
    if (!organizationId) return false;
    const ok = await completeStore(chatKey, organizationId);
    if (!ok) toast.error('Non sono riuscito a chiudere il promemoria. Riprova tra poco.');
    else toast.success(reminder?.taskId ? 'Fatto: Task commerciale completato' : 'Fatto: promemoria chiuso');
    return ok;
  }, [organizationId, completeStore, chatKey, reminder?.taskId]);

  return { reminder, linked, canSave, schedule, complete };
}
